import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/analytics-kit";
import Auth from "../../functions/authenticate";
import ServiceFactory from "../../service";
import { hasTenantFeature } from "../../functions/tenant-features";

/*
 * Apps hub (mockups/apps-redesign-2026-10-09.html, board "1 · Apps").
 * A grid of cards: icon, name, a plain one-sentence description, a status
 * chip and one status line. The whole card links to the app.
 *
 * Each card has one loader that returns { chip, tone, line }. Loaders run
 * in parallel and fail soft: a failed call shows a neutral line, never a
 * blank page. New apps append to APPS; they never add menu items.
 */

function svc(api, ep) {
  const Service = ServiceFactory.getType(api, ep);
  return new Service().getAsync();
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtDay(d) {
  const x = new Date(d);
  if (!d || isNaN(x.getTime())) return "";
  const today = new Date();
  const start = t => new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  const diff = Math.round((start(today) - start(x)) / 86400000);
  if (diff === 0) return "today";
  if (diff === 1) return "yesterday";
  return `${MONTHS[x.getMonth()]} ${x.getDate()}${x.getFullYear() !== today.getFullYear() ? `, ${x.getFullYear()}` : ""}`;
}
function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many || one + "s"}`;
}
function listOf(rows) {
  return Array.isArray(rows) ? rows : rows && (rows.items || rows.data) || [];
}

// Report-style apps: count + newest row in one call ($count=true returns { count, items }).
function reportLoader(api, nameOf) {
  return async () => {
    const res = await svc(`${api}?$top=1&$count=true&$orderByDesc=created_date`, "research");
    const items = listOf(res);
    const count = res && typeof res.count === "number" ? res.count : items.length;
    const r = items[0];
    if (!r) return {
      chip: "No reports",
      tone: "neutral",
      line: "No reports yet. Run your first one."
    };
    const day = fmtDay(r.createdDate);
    return {
      chip: plural(count, "report"),
      tone: "blue",
      line: `Latest: ${nameOf(r) || "untitled"}${day ? `, ${day}` : ""}`
    };
  };
}

// Site checks: first active site (the one its page opens on), open issues
// grouped by check id, pages by URL without the query string.
function siteCheckLoader(kind, problemTone) {
  return async () => {
    const sites = listOf(await svc(`/${kind}/sites`, "product")).filter(s => s.status !== "Inactive");
    if (!sites.length) return {
      chip: "Not set up",
      tone: "neutral",
      line: "Add your site to start checking it"
    };
    const site = sites[0];
    const issues = listOf(await svc(`/${kind}/sites/${site.id}/issues?$top=500`, "product"));
    const open = issues.filter(i => {
      const s = String(i.status || "").toLowerCase();
      return s === "open" || s === "new";
    });
    const rules = new Set(open.map(i => String(i.ruleId || i.checkId || i.issueHash)));
    const pages = new Set(open.map(i => String(i.page || i.url || "").split("?")[0]).filter(Boolean));
    const dates = [site.lastScanDate, site.lastScannedDate, site.lastCheckedDate, ...issues.map(i => i.lastSeenDate || i.updatedDate)].filter(Boolean).sort((a, b) => new Date(a) - new Date(b));
    const checked = fmtDay(dates[dates.length - 1]);
    const when = checked ? ` · checked ${checked}` : "";
    if (!rules.size) return {
      chip: "All clear",
      tone: "green",
      line: `No problems found${when}`
    };
    return {
      chip: `${rules.size} to fix`,
      tone: problemTone,
      line: `${plural(rules.size, "problem")} on ${plural(pages.size, "page")}${when}`
    };
  };
}
const APPS = [{
  // Feature-gated (registry `dataEnrichment`, default OFF: unmetered
  // vendor spend; enable per tenant deliberately).
  id: "enrich",
  icon: "person_search",
  name: "Data Enrichment",
  line: "Fills in missing details, like titles, phone numbers and company facts, on any list of contacts or companies.",
  route: "/DataEnrichment",
  feature: "dataEnrichment",
  load: async () => {
    const jobs = listOf(await svc("/person/lookup", "audience"));
    const done = jobs.filter(j => String(j.status).toLowerCase() === "completed");
    return done.length ? {
      chip: plural(done.length, "list"),
      tone: "blue",
      line: `${plural(done.length, "list")} filled in so far`
    } : {
      chip: "No lists yet",
      tone: "neutral",
      line: "Upload your first list"
    };
  }
}, {
  id: "company",
  icon: "data_exploration",
  name: "Company Analysis",
  line: "A quick read on any company: what it sells, who it competes with, how visible it is online.",
  route: "/Research/Company",
  load: reportLoader("/company-analysis", r => r.company || r.domain)
}, {
  id: "trends",
  icon: "query_stats",
  name: "Market Trends",
  line: "How much people search for a topic over time, where interest is rising and what to do about it.",
  route: "/Research/Trends",
  load: reportLoader("/market-trends", r => r.term)
}, {
  id: "keywords",
  icon: "manage_search",
  name: "Keyword Intelligence",
  line: "Where you show up in Google and Bing for the searches that matter to you, against your competitors.",
  route: "/Keywords",
  load: async () => {
    // overview = one row per term and search engine; null position = not in the top 100.
    const [rows, comps] = await Promise.all([svc("/keywords/overview", "research").then(listOf), svc("/keywords/competitors", "research").then(listOf).catch(() => null)]);
    const best = new Map();
    rows.forEach(r => {
      const key = String(r.keywordId != null ? r.keywordId : r.term);
      const p = r.currentPosition == null ? null : Number(r.currentPosition);
      if (!best.has(key)) best.set(key, null);
      if (p != null && !isNaN(p) && (best.get(key) == null || p < best.get(key))) best.set(key, p);
    });
    const total = best.size;
    if (!total) return {
      chip: "Not set up",
      tone: "neutral",
      line: "Add the searches you want to track"
    };
    // Ranks are measured on the "self" site; without one every term reads as unranked.
    if (comps && !comps.some(c => c.isSelf)) return {
      chip: "Needs your site",
      tone: "amber",
      line: "Tell us which website is yours to see where you rank"
    };
    const ranked = [...best.values()].filter(p => p != null);
    const top10 = ranked.filter(p => p <= 10).length;
    const line = ranked.length ? `${ranked.length} of ${plural(total, "term")} rank, best #${Math.min(...ranked)}` : `You aren't in the top 100 for any of your ${plural(total, "term")} yet`;
    return top10 ? {
      chip: `${top10} in the top 10`,
      tone: "green",
      line
    } : {
      chip: "Needs attention",
      tone: "amber",
      line
    };
  }
}, {
  id: "ai-visibility",
  icon: "psychology",
  name: "AI Visibility",
  line: "Whether ChatGPT, Claude, Gemini and Perplexity mention you when buyers ask questions.",
  route: "/AiVisibility",
  load: async () => {
    // Latest answer per question and assistant for the ACTIVE questions, and a
    // mention = mentioned or linked: the same count as the AI Visibility page.
    const [rows, prompts, comps] = await Promise.all([svc("/keywords/ai/visibility?days=90", "research").then(listOf), svc("/keywords/ai/prompts", "research").then(listOf), svc("/keywords/competitors", "research").then(listOf).catch(() => null)]);
    const activeIds = new Set(prompts.filter(p => p.active !== false).map(p => String(p.id)));
    const cells = new Map();
    rows.filter(r => activeIds.has(String(r.promptId))).forEach(r => {
      const key = `${String(r.promptId)}|${String(r.provider).toLowerCase()}`;
      const prev = cells.get(key);
      if (!prev || new Date(r.checkedDate) > new Date(prev.checkedDate)) cells.set(key, r);
    });
    const total = cells.size;
    if (!total) return {
      chip: "Not set up",
      tone: "neutral",
      line: "Add the questions your buyers ask"
    };
    if (comps && !comps.some(c => c.isSelf)) return {
      chip: "Needs your site",
      tone: "amber",
      line: "Tell us which website is yours so mentions can be counted"
    };
    const hits = [...cells.values()].filter(c => c.mentioned || c.cited).length;
    if (!hits) return {
      chip: "Needs attention",
      tone: "amber",
      line: `Not mentioned in any of ${plural(total, "answer")}`
    };
    const line = `Mentioned in ${hits} of ${plural(total, "answer")}`;
    return hits / total < 0.25 ? {
      chip: "Needs attention",
      tone: "amber",
      line
    } : {
      chip: `${Math.round(hits / total * 100)}% mentioned`,
      tone: "green",
      line
    };
  }
}, {
  id: "ada",
  icon: "accessibility_new",
  name: "Accessibility checks",
  line: "Finds things on your site that make it hard to use for people with disabilities.",
  route: "/Ada",
  load: siteCheckLoader("ada", "red")
}, {
  id: "seo",
  icon: "travel_explore",
  name: "SEO checks",
  line: "Finds things on your site that hold back how search engines read and rank it.",
  route: "/Seo",
  load: siteCheckLoader("seo", "amber")
}];
const FAILED = {
  chip: null,
  tone: "neutral",
  line: "Status isn't available right now"
};
class AppsViewComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      status: {}
    };
    this._alive = true;
  }

  // Feature-gated cards only render (and only load status) when the
  // tenant's registry Features carries the flag.
  get visibleApps() {
    return APPS.filter(app => !app.feature || hasTenantFeature(app.feature));
  }
  async componentDidMount() {
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    this.visibleApps.forEach(app => {
      Promise.resolve().then(app.load).then(s => s || FAILED, () => FAILED).then(s => {
        if (this._alive) this.setState(st => ({
          status: {
            ...st.status,
            [app.id]: s
          }
        }));
      });
    });
  }
  componentWillUnmount() {
    this._alive = false;
  }
  render() {
    const {
      status
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view aph"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      title: "Apps",
      subtitle: "Tools that research your market and check your website"
    }), /*#__PURE__*/React.createElement("div", {
      className: "aph-grid"
    }, this.visibleApps.map(app => {
      const s = status[app.id];
      return /*#__PURE__*/React.createElement(Link, {
        key: app.id,
        to: app.route,
        className: "aph-card",
        "aria-label": `Open ${app.name}`
      }, /*#__PURE__*/React.createElement("div", {
        className: "aph-top"
      }, /*#__PURE__*/React.createElement("span", {
        className: "aph-icon material-icons",
        "aria-hidden": "true"
      }, app.icon), /*#__PURE__*/React.createElement("b", {
        className: "aph-name"
      }, app.name), s ? s.chip ? /*#__PURE__*/React.createElement("span", {
        className: `aph-chip aph-chip-${s.tone}`
      }, s.chip) : null : /*#__PURE__*/React.createElement("span", {
        className: "aph-chip aph-chip-loading"
      }, "\u2026")), /*#__PURE__*/React.createElement("p", {
        className: "aph-desc"
      }, app.line), /*#__PURE__*/React.createElement("p", {
        className: "aph-status" + (!s || s === FAILED ? " aph-status-quiet" : "")
      }, s ? s.line : "…"));
    })));
  }
}
export default withRouter(AppsViewComponent);